import React from 'react';
import { useNavigate } from 'react-router-dom';
import SingleReportPurchase from '../components/Payment/SingleReportPurchase';
import { trackMapLaunch, trackReportDownload, trackEnterpriseInquiry } from '../utils/analytics';
import { useLanguage } from '../context/LanguageContext';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleLaunchMap = () => {
    trackMapLaunch('homepage');
    navigate('/map');
  };

  const handlePermitReview = () => {
    navigate('/permitreview');
  };

  const handleViewSample = () => {
    trackReportDownload('sample');
    const link = document.createElement('a');
    link.href = '/zoning-analysis-sample.pdf';
    link.download = 'ZoningPal-Sample-Report.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePurchaseSuccess = () => {
    navigate('/map');
  };

  const handlePurchaseError = (error: string) => {
    console.error('Purchase error:', error);
  };

  return (
    <div className="homepage">
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-logo">
                <img
                  src="/DES-20250328_Logo_v3_SS.png"
                  alt="ZoningPal Logo"
                  className="logo-image"
                />
              </div>
              <h1 className="hero-title">
                Get Instant AI Zoning Analysis for Any Property in Toronto
              </h1>
              <p className="hero-subtitle">
                Stop digging through hundreds of pages of by-laws. Get your comprehensive zoning report in seconds.
              </p>
              <div className="hero-cta">
                <button
                  className="btn-primary launch-map-btn"
                  onClick={handleLaunchMap}
                >
                  Launch Interactive Map
                </button>
                <button
                  className="btn-secondary"
                  onClick={handleViewSample}
                >
                  Download Sample Report
                </button>
                <button
                  className="btn-secondary permit-review-cta"
                  onClick={handlePermitReview}
                >
                  {t.home.heroPermitCta}
                </button>
              </div>
            </div>
            <div className="hero-visual">
              <img
                src="/MKT_20251020_StoreFront_v1.png"
                alt="ZoningPal App Interface"
                className="hero-app-image"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="acc-prototype-section">
        <div className="container">
          <div className="acc-prototype-card">
            <div className="acc-prototype-copy">
              <p className="acc-prototype-eyebrow">{t.common.permitReview}</p>
              <h2>{t.home.accTitle}</h2>
              <p className="acc-prototype-subtitle">{t.home.accSubtitle}</p>
              <ul className="acc-prototype-bullets">
                <li>{t.home.accBullet1}</li>
                <li>{t.home.accBullet2}</li>
                <li>{t.home.accBullet3}</li>
                <li>{t.home.accBullet4}</li>
                <li>{t.home.accBullet5}</li>
                <li>{t.home.accBullet6}</li>
              </ul>
              <p className="acc-prototype-note">{t.home.accNote}</p>
              <button className="btn-primary" onClick={handlePermitReview}>
                {t.home.accCta}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="features-section">
        <div className="container">
          <div className="features-content">
            <div className="features-text">
              <h2>How It Works</h2>
              <div className="steps-compact">
                <div className="step-compact">
                  <span className="step-number">1</span>
                  <span>Search any Toronto address</span>
                </div>
                <div className="step-compact">
                  <span className="step-number">2</span>
                  <span>AI analyzes zoning by-laws</span>
                </div>
                <div className="step-compact">
                  <span className="step-number">3</span>
                  <span>Get comprehensive report</span>
                </div>
              </div>

              <div className="report-preview-compact">
                <img
                  src="/MKT_20251020_StoreFront_v2.png"
                  alt="Sample Zoning Report"
                  className="report-preview-image"
                />
                <div className="report-features-inline">
                  <h3>Every Report Includes:</h3>
                  <ul>
                    <li>✓ Zoning district classification</li>
                    <li>✓ Development potential analysis</li>
                    <li>✓ Parking & setback requirements</li>
                    <li>✓ Heritage considerations</li>
                  </ul>
                  <button
                    className="btn-secondary"
                    onClick={handleViewSample}
                  >
                    Download Sample Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="purchase-section">
        <div className="container">
          <h2 className="section-title">Get Your Zoning Report</h2>
          <p className="section-subtitle">Choose the plan that works for you</p>
          <div className="purchase-grid">
            <div className="purchase-card">
              <SingleReportPurchase
                onSuccess={handlePurchaseSuccess}
                onError={handlePurchaseError}
              />
            </div>

            <div className="purchase-card enterprise-card">
              <div className="purchase-card-header">
                <h3 className="purchase-card-title">Enterprise Solutions</h3>
                <div className="purchase-card-price">Custom</div>
                <p className="purchase-card-period">Contact for pricing</p>
              </div>

              <div className="purchase-card-content">
                <p className="purchase-card-description">
                  Custom pricing for large-scale needs and enterprise requirements
                </p>

                <ul className="purchase-card-features">
                  <li>✓ Bulk analysis capabilities</li>
                  <li>✓ Custom integration options</li>
                  <li>✓ Priority support</li>
                  <li>✓ Volume discounts</li>
                  <li>✓ Dedicated account management</li>
                </ul>
              </div>

              <div className="purchase-card-footer">
                <a
                  href="mailto:sales@zoningpal.com?subject=Enterprise Pricing Inquiry"
                  className="purchase-card-button"
                  onClick={() => trackEnterpriseInquiry('homepage')}
                >
                  Contact Sales
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="final-cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>Join hundreds of Toronto professionals who trust ZoningPal for their zoning analysis needs.</p>
            <div className="final-cta-buttons">
              <button className="btn-primary launch-map-btn" onClick={handleLaunchMap}>
                Analyze Your First Property
              </button>
              <button className="btn-secondary permit-review-cta" onClick={handlePermitReview}>
                {t.home.heroPermitCta}
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="homepage-footer">
        <div className="container homepage-footer-inner">
          <span>{t.common.appName}</span>
          <nav className="homepage-footer-nav">
            <button type="button" onClick={() => navigate('/map')}>
              Map
            </button>
            <button type="button" onClick={handlePermitReview}>
              {t.common.permitReview}
            </button>
            <button type="button" onClick={() => navigate('/pricing')}>
              {t.common.pricing}
            </button>
            <button type="button" onClick={() => navigate('/contact')}>
              {t.common.contact}
            </button>
            <button type="button" onClick={() => navigate('/legal')}>
              {t.common.legal}
            </button>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
